<template>
  <v-dialog
    :value="value"
    max-width="640"
    persistent
    scrollable
    :retain-focus="false"
    @input="$emit('input', $event)"
  >
    <v-card dark>
      <v-card-title>Add Permissions</v-card-title>
      <v-card-text>
        <v-select
          v-model="pickYear"
          :items="yearOptions"
          label="Year"
          dense
          outlined
          class="mt-2"
          :menu-props="menuProps"
          @change="loadEventsForYear"
        />
        <v-autocomplete
          v-model="pickEventId"
          :items="yearEventOptions"
          item-text="label"
          item-value="EventId"
          label="Event"
          placeholder="Type to search…"
          dense
          outlined
          hide-no-data
          :loading="loadingYearEvents"
          :filter="filterEventOption"
          :menu-props="menuProps"
          @change="onEventSelected"
        />
        <v-select
          v-model="pickPermission"
          :items="permissionOptions"
          label="Event permission"
          dense
          outlined
          :menu-props="menuProps"
        />
        <v-autocomplete
          v-model="pickShId"
          :items="stallholderOptions"
          item-text="label"
          item-value="StallholderId"
          label="Stallholder (optional)"
          placeholder="Optional"
          dense
          outlined
          clearable
          hide-no-data
          :loading="loadingEventMeta"
          :disabled="!pickEventId"
          :menu-props="menuProps"
          @change="onStallholderSelected"
        />
        <v-select
          v-if="pickShId"
          v-model="pickShPermission"
          :items="permissionOptions"
          label="Stallholder permission"
          dense
          outlined
          :menu-props="menuProps"
        />
        <v-autocomplete
          v-model="pickStallId"
          :items="stallOptions"
          item-text="label"
          item-value="StallId"
          label="Stall (optional)"
          placeholder="Optional"
          dense
          outlined
          clearable
          hide-no-data
          :disabled="!pickShId"
          :menu-props="menuProps"
        />
        <v-select
          v-if="pickStallId"
          v-model="pickStallPermission"
          :items="permissionOptions"
          label="Stall permission"
          dense
          outlined
          :menu-props="menuProps"
        />

        <div v-if="sessionAdds.length" class="mt-4">
          <div class="caption grey--text mb-2">Added this session</div>
          <v-list dense dark>
            <v-list-item
              v-for="(row, idx) in sessionAdds"
              :key="row.key"
              class="px-0"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ row.label }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ row.permission }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="removeSessionAdd(idx)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Done</v-btn>
        <v-btn
          color="secondary"
          :disabled="!pickEventId"
          :loading="loadingEventMeta"
          @click="addCurrent"
        >
          Add
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { dashboardAPI } from "@/services";
import iziToast from "izitoast/dist/js/iziToast.min.js";

const PERMS = ["Write", "Read", "Stock", "None"];

export default {
  name: "AddPermissionsDialog",
  props: {
    value: { type: Boolean, default: false },
    events: { type: Array, default: () => [] },
    defaultYear: { type: [Number, String], default: null },
  },
  data() {
    const y = new Date().getFullYear();
    return {
      yearOptions: [y + 1, y, y - 1, y - 2, y - 3, y - 4, y - 5],
      permissionOptions: PERMS,
      menuProps: { zIndex: 3000, offsetY: true, maxHeight: 280 },
      pickYear: y,
      pickEventId: null,
      pickPermission: "Read",
      pickShId: null,
      pickShPermission: "Read",
      pickStallId: null,
      pickStallPermission: "Read",
      yearEvents: [],
      loadingYearEvents: false,
      stallholderOptions: [],
      stallOptions: [],
      loadingEventMeta: false,
      eventMeta: null,
      sessionAdds: [],
      createdEventIds: [],
    };
  },
  computed: {
    yearEventOptions() {
      return (this.yearEvents || []).map((e) => ({
        EventId: e.EventId,
        EventName: e.EventName || e.Name || "",
        label: `${e.EventName || e.Name || "Unnamed"} (${e.EventId})`,
      }));
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      const y = this.defaultYear || new Date().getFullYear();
      this.pickYear = Number(y);
      this.pickEventId = null;
      this.pickPermission = "Read";
      this.pickShId = null;
      this.pickShPermission = "Read";
      this.pickStallId = null;
      this.pickStallPermission = "Read";
      this.stallholderOptions = [];
      this.stallOptions = [];
      this.eventMeta = null;
      this.sessionAdds = [];
      this.createdEventIds = [];
      this.loadEventsForYear();
    },
    close() {
      this.$emit("input", false);
    },
    filterEventOption(item, queryText, itemText) {
      const q = (queryText || "").toLowerCase();
      if (!q) return true;
      const label = (itemText || item.label || item.EventName || "").toLowerCase();
      const id = (item.EventId || "").toLowerCase();
      return label.includes(q) || id.includes(q);
    },
    eventName(eventId) {
      const opt = this.yearEventOptions.find((e) => e.EventId === eventId);
      return opt?.EventName || eventId;
    },
    stallholderName(shId) {
      const sh = (this.stallholderOptions || []).find(
        (s) => s.StallholderId === shId
      );
      return sh?.StallholderName || shId;
    },
    stallName(stallId) {
      const st = (this.stallOptions || []).find((s) => s.StallId === stallId);
      return st?.StallName || stallId;
    },
    async loadEventsForYear() {
      this.loadingYearEvents = true;
      this.yearEvents = [];
      this.pickEventId = null;
      this.clearShStall();
      try {
        const api = dashboardAPI.getAdminGroupsEventsAPI();
        const data = await api.getEventsData(this.pickYear);
        this.yearEvents = Array.isArray(data) ? data : data?.Events || [];
        if (!this.yearEvents.length) {
          iziToast.warning({
            title: "Events",
            message: `No events found for ${this.pickYear}`,
            position: "topRight",
          });
        }
      } catch (e) {
        this.yearEvents = [];
        iziToast.error({
          title: "Events",
          message: "Failed to load events for year",
          position: "topRight",
        });
      } finally {
        this.loadingYearEvents = false;
      }
    },
    clearShStall() {
      this.pickShId = null;
      this.pickStallId = null;
      this.stallholderOptions = [];
      this.stallOptions = [];
      this.eventMeta = null;
    },
    async onEventSelected() {
      this.pickShId = null;
      this.pickStallId = null;
      this.stallOptions = [];
      this.eventMeta = null;
      this.stallholderOptions = [];
      if (!this.pickEventId) return;
      this.loadingEventMeta = true;
      try {
        const data = await dashboardAPI
          .getDashboardAccessAPI()
          .resolvePermissions({
            EventIds: [this.pickEventId],
            includeCatalog: true,
          });
        this.eventMeta = (data.Events || {})[this.pickEventId] || null;
        this.stallholderOptions = (this.eventMeta?.Stallholders || []).map(
          (sh) => ({
            StallholderId: sh.StallholderId,
            StallholderName: sh.StallholderName,
            label: `${sh.StallholderName || "Unnamed"} (${sh.StallholderId})`,
            Stalls: sh.Stalls || [],
          })
        );
      } catch (e) {
        this.eventMeta = null;
        this.stallholderOptions = [];
        iziToast.error({
          title: "Permissions",
          message: "Failed to load stallholders for event",
          position: "topRight",
        });
      } finally {
        this.loadingEventMeta = false;
      }
    },
    onStallholderSelected() {
      this.pickStallId = null;
      const sh = (this.stallholderOptions || []).find(
        (s) => s.StallholderId === this.pickShId
      );
      this.stallOptions = (sh?.Stalls || []).map((st) => ({
        StallId: st.StallId,
        StallName: st.StallName,
        label: `${st.StallName || "Unnamed"} (${st.StallId})`,
      }));
    },
    cloneEvents() {
      return JSON.parse(JSON.stringify(this.events || []));
    },
    emitEvents(next) {
      this.$emit("update:events", next);
      this.$emit("added", next);
    },
    ensureEvent(next, eventId, permission, overwritePermission) {
      let ev = next.find((e) => e.EventId === eventId);
      const created = !ev;
      if (!ev) {
        ev = {
          EventId: eventId,
          Permission: permission,
          Stallholders: [],
        };
        next.unshift(ev);
        if (!this.createdEventIds.includes(eventId)) {
          this.createdEventIds.push(eventId);
        }
      } else {
        if (!Array.isArray(ev.Stallholders)) ev.Stallholders = [];
        if (overwritePermission) ev.Permission = permission;
      }
      return { ev, created };
    },
    addCurrent() {
      if (!this.pickEventId) return;
      const next = this.cloneEvents();
      const existed = next.some((e) => e.EventId === this.pickEventId);
      if (existed && !this.pickShId) {
        iziToast.warning({
          title: "Permissions",
          message: "This event is already added — pick a stallholder or stall",
          position: "topRight",
        });
        return;
      }
      const { ev, created } = this.ensureEvent(
        next,
        this.pickEventId,
        this.pickPermission,
        !existed
      );

      const eventLabel = `${this.eventName(this.pickEventId)} (${this.pickEventId})`;
      const rows = [];

      if (created) {
        rows.push({
          key: `event-${this.pickEventId}-${Date.now()}`,
          type: "event",
          EventId: this.pickEventId,
          permission: ev.Permission,
          label: `Event: ${eventLabel}`,
        });
      }

      if (this.pickShId) {
        let sh = ev.Stallholders.find((s) => s.StallholderId === this.pickShId);
        const shCreated = !sh;
        if (!sh) {
          sh = {
            StallholderId: this.pickShId,
            Permission: this.pickShPermission,
            Stalls: [],
          };
          ev.Stallholders.push(sh);
        } else {
          sh.Permission = this.pickShPermission;
          if (!Array.isArray(sh.Stalls)) sh.Stalls = [];
        }
        if (shCreated) {
          rows.push({
            key: `sh-${this.pickEventId}-${this.pickShId}-${Date.now()}`,
            type: "stallholder",
            EventId: this.pickEventId,
            StallholderId: this.pickShId,
            permission: this.pickShPermission,
            label: `Stallholder: ${this.stallholderName(this.pickShId)}`,
          });
        } else {
          rows.push({
            key: `sh-${this.pickEventId}-${this.pickShId}-upd-${Date.now()}`,
            type: "stallholder-update",
            EventId: this.pickEventId,
            StallholderId: this.pickShId,
            permission: this.pickShPermission,
            label: `Stallholder: ${this.stallholderName(this.pickShId)}`,
          });
        }

        if (this.pickStallId) {
          const stallExists = sh.Stalls.find((s) => s.StallId === this.pickStallId);
          if (!stallExists) {
            sh.Stalls.push({
              StallId: this.pickStallId,
              Permission: this.pickStallPermission,
            });
            rows.push({
              key: `st-${this.pickEventId}-${this.pickShId}-${this.pickStallId}-${Date.now()}`,
              type: "stall",
              EventId: this.pickEventId,
              StallholderId: this.pickShId,
              StallId: this.pickStallId,
              permission: this.pickStallPermission,
              label: `Stall: ${this.stallName(this.pickStallId)}`,
            });
          } else {
            stallExists.Permission = this.pickStallPermission;
            rows.push({
              key: `st-${this.pickEventId}-${this.pickShId}-${this.pickStallId}-upd-${Date.now()}`,
              type: "stall-update",
              EventId: this.pickEventId,
              StallholderId: this.pickShId,
              StallId: this.pickStallId,
              permission: this.pickStallPermission,
              label: `Stall: ${this.stallName(this.pickStallId)}`,
            });
          }
        }
      }

      this.emitEvents(next);
      this.sessionAdds = [...this.sessionAdds, ...rows];
      iziToast.success({
        title: "Permissions",
        message: "Added — you can add more or click Done",
        position: "topRight",
      });
      this.pickShId = null;
      this.pickStallId = null;
      this.stallOptions = [];
    },
    removeSessionAdd(idx) {
      const row = this.sessionAdds[idx];
      if (!row) return;
      const next = this.cloneEvents();
      const ev = next.find((e) => e.EventId === row.EventId);

      if (row.type === "event" && this.createdEventIds.includes(row.EventId)) {
        this.emitEvents(next.filter((e) => e.EventId !== row.EventId));
        this.createdEventIds = this.createdEventIds.filter(
          (id) => id !== row.EventId
        );
        this.sessionAdds = this.sessionAdds.filter(
          (r) => r.EventId !== row.EventId
        );
        return;
      }

      if (ev && (row.type === "stall" || row.type === "stall-update")) {
        ev.Stallholders = (ev.Stallholders || []).map((sh) => {
          if (sh.StallholderId !== row.StallholderId) return sh;
          return {
            ...sh,
            Stalls: (sh.Stalls || []).filter((s) => s.StallId !== row.StallId),
          };
        });
        this.emitEvents(next);
        this.sessionAdds = this.sessionAdds.filter((r) => r.key !== row.key);
        return;
      }

      if (
        ev &&
        (row.type === "stallholder" || row.type === "stallholder-update")
      ) {
        ev.Stallholders = (ev.Stallholders || []).filter(
          (sh) => sh.StallholderId !== row.StallholderId
        );
        this.emitEvents(next);
        this.sessionAdds = this.sessionAdds.filter(
          (r) =>
            !(
              r.EventId === row.EventId &&
              r.StallholderId === row.StallholderId
            )
        );
      }
    },
  },
};
</script>
